import {Injectable} from '@angular/core';
import {Utils} from '../utils/utils';
import {BehaviorSubject} from "rxjs";

@Injectable({
  providedIn: 'root',
})
export class IndexedDBService {
  private dbName = 'collectedDataDB';

  public isInitialized: BehaviorSubject<boolean> = new BehaviorSubject(false);

  public static readonly Tables = {
    DATA: 'data'
  } as const;

  public static readonly DataKeys = {
    GATEWAY_ID: 'gatewayId',
    BUILDING: 'building',
    GENERAL_GATEWAY: 'generalGateway',
    GENERAL_MEASUREMENT: 'generalMeasurement',
    GENERAL_PRE_PHOTOS: 'generalPrePhotos',
    GENERAL_SENDING: 'generalSending',
    HEATING_CHECK: 'heatingCheck',
    HEATING_INFORMATION: 'heatingInformation',
    PHOTOS: 'photos',
    SENSORS_COUNTER: 'sensorsCounter',
    SENSORS_OTHER: 'sensorsOther',
    SENSORS_OUTSIDE_TEMP: 'sensorsOutsideTemp',
    SENSORS_PUMP: 'sensorsPump',
    SENSORS_TEMPERATURE: 'sensorsTemperature'

  } as const;

  private db!: IDBDatabase;
  private unsavedData: { [key: string]: { data: any, table: string } } = {};
  private debugMode = false;

  constructor() {
  }

  private log(message: string, ...optionalParams: any[]) {
    if (this.debugMode) {
      console.log(message, ...optionalParams);
    }
  }

  private warn(message: string, ...optionalParams: any[]) {
    if (this.debugMode) {
      console.warn(message, ...optionalParams);
    }
  }

  initDB(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 3);
      request.onupgradeneeded = (event: any) => {
        this.db = event.target.result;
        if (!this.db.objectStoreNames.contains(IndexedDBService.Tables.DATA)) {
          this.db.createObjectStore(IndexedDBService.Tables.DATA, {keyPath: null});
        }
      };
      request.onsuccess = (event: any) => {
        this.db = event.target.result;
        this.isInitialized.next(true);
        console.log('Database initialized successfully');
        resolve(true);
      };
      request.onerror = (event) => {
        console.error('Error initializing database:', event);
        reject(false);
      };
    });
  }

  getAllData(table: string): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(table, 'readonly');
      const store = transaction.objectStore(table);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  getData(table: string, key: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(table, 'readonly');
      const store = transaction.objectStore(table);
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  setData(data: any, table: string, key: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db.transaction(table, 'readwrite');
        const store = transaction.objectStore(table);
        const request = store.put(data, key);

        request.onsuccess = () => {
          this.log('Data saved successfully', key);
          // Remove from unsaved data if it was previously there
          if (this.unsavedData[key]) {
            delete this.unsavedData[key];
          }
          resolve();
        };

        request.onerror = () => {
          this.warn('Error saving data to IndexedDB', key, request.error);
          // Track unsaved data
          this.unsavedData[key] = {data: Utils.deepCopy(data), table};
          reject(request.error);
        };

        transaction.onerror = () => {
          this.warn('Transaction error for key', key, transaction.error);
          this.unsavedData[key] = {data: Utils.deepCopy(data), table};
          reject(transaction.error);
        };
      } catch (e) {
        this.warn('Exception while saving data to IndexedDB', key, e);
        // Track unsaved data on exception
        this.unsavedData[key] = {data: Utils.deepCopy(data), table};
        reject(e);
      }
    });
  }

  deleteData(table: string, key: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(table, 'readwrite');
      const store = transaction.objectStore(table);
      const request = store.delete(key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  public someDataWasNotSaved(): boolean {
    return Object.keys(this.unsavedData).length > 0;
  }

  public getUnsavedData(): { [key: string]: { data: any, table: string } } {
    return this.unsavedData;
  }

  public clearUnsavedData(): void {
    this.unsavedData = {};
  }

  public async retrySavingUnsavedData(): Promise<void> {
    const unsavedKeys = Object.keys(this.unsavedData);
    if (unsavedKeys.length === 0) {
      this.log('No unsaved data to retry');
      return;
    }

    this.log('Attempting to retry saving unsaved data', unsavedKeys.length, 'items');

    for (const key of unsavedKeys) {
      const {data, table} = this.unsavedData[key];
      try {
        await this.setData(data, table, key);
        this.log('Successfully retried saving data for key:', key);
      } catch (e) {
        this.warn('Failed to retry saving data for key:', key, e);
      }
    }
  }
}
